'use client';

import React from 'react';
import { useRouter } from 'next/navigation';

interface Service {
  id: string;
  icon: string;
  title: string;
  desc: string;
  image: string;
  alt: string;
  startingPrice: string;
  priceNote?: string;
  addonBadge?: string;
  customBadge?: boolean;
  isTrashBin?: boolean;
  isDriveway?: boolean;
  isWindow?: boolean;
  isHouseWash?: boolean;
}

const services: Service[] = [
  {
    id: 'driveway',
    icon: '🏗️',
    title: 'Driveway & Concrete Cleaning',
    desc: 'Blast away years of oil stains, tire marks, and grime from driveways, walkways, and concrete surfaces. Restore your curb appeal instantly.',
    image: '/assets/images/driveway_cleaning_hero.png',
    alt: 'Professional contractor using commercial surface cleaner on upscale New Mexico home driveway with dramatic clean vs dirty contrast and wet sheen at golden hour',
    startingPrice: '$249.99',
    priceNote: 'Final price depends on the size, condition, accessibility, and overall scope of the project.',
    isDriveway: true,
  },
  {
    id: 'house-wash',
    icon: '🏠',
    title: 'House Washing',
    desc: "Soft wash your home's exterior to safely remove mold, mildew, algae, and dirt without damaging siding, paint, or surfaces.",
    image: '/assets/images/house_washing_hero.png',
    alt: 'Professional technician applying soft wash to luxury New Mexico stucco home in Rio Rancho with dramatic before-and-after split showing dull dirty left side and freshly cleaned right side, Sandia Mountains in background at golden hour',
    startingPrice: '$199',
    isHouseWash: true,
  },
  {
    id: 'windows',
    icon: '🪟',
    title: 'Window Cleaning',
    desc: 'Crystal-clear windows inside and out. We remove hard water stains, dirt, and streaks for a spotless finish that lets the light in.',
    image: '/assets/images/window_cleaning_hero.png',
    alt: 'Professional Rio Grande Property Solutions technician using water-fed pole to clean large floor-to-ceiling windows on luxury Rio Rancho New Mexico stucco home at golden hour',
    startingPrice: '$129',
    isWindow: true,
  },
  {
    id: 'solar',
    icon: '☀️',
    title: 'Solar Panel Cleaning',
    desc: 'Dirty solar panels lose up to 30% efficiency. Our gentle cleaning restores maximum energy output and protects your investment.',
    image: '/assets/images/solar_panel_cleaning_hero.png',
    alt: 'Professional Rio Grande Property Solutions technician in black polo and safety harness using carbon fiber water-fed pole to clean rooftop solar panels on luxury Rio Rancho New Mexico stucco home at golden hour',
    startingPrice: '$149',
    priceNote: 'Final price depends on the number of panels, roof accessibility, and overall condition.',
  },
  {
    id: 'roof',
    icon: '🏚️',
    title: 'Roof Cleaning',
    desc: 'Remove black streaks, moss, lichen, and algae from your roof with our safe, low-pressure soft wash method that extends roof life.',
    image: '/assets/images/roof_cleaning_hero.png',
    alt: 'Professional Rio Grande Property Solutions technician in black polo, safety harness, and black cap using soft wash wand to clean clay tile roof on luxury Rio Rancho New Mexico stucco home',
    startingPrice: '$299',
  },
  {
    id: 'gutters',
    icon: '🌊',
    title: 'Gutter Cleaning',
    desc: "Clogged gutters cause water damage. We clear debris, flush downspouts, and ensure proper drainage to protect your home's foundation.",
    image: '/assets/images/gutter_cleaning_hero.png',
    alt: 'Professional Rio Grande Property Solutions technician in black polo, safety harness, and black cap working from extension ladder with stabilizers to clean gutters on luxury Rio Rancho New Mexico stucco home',
    startingPrice: '$149',
  },
  {
    id: 'trash-bin',
    icon: '🗑️',
    title: 'Trash Bin Cleaning',
    desc: 'Eliminate bacteria, odors, and grime from your trash and recycling bins. Sanitized bins mean a cleaner, healthier home environment.',
    image: '/assets/images/trash_bin_cleaning_hero.png',
    alt: 'Professional Rio Grande Property Solutions technician in black polo, black cap, khaki pants and nitrile gloves actively pressure washing a trash bin with commercial turbo-nozzle pressure washer on luxury Rio Rancho New Mexico stucco home driveway',
    startingPrice: '$35 per bin',
    addonBadge: '$25 when added to any other service',
    isTrashBin: true,
  },
  {
    id: 'commercial',
    icon: '🏢',
    title: 'Commercial Property Cleaning',
    desc: 'First impressions matter. Keep your business exterior spotless with our commercial pressure washing for storefronts, parking lots, and more.',
    image: '/assets/images/commercial_property_cleaning_hero.png',
    alt: 'Three professional Rio Grande Property Solutions technicians in black polos, black hats, khaki pants, safety glasses working simultaneously on a modern Albuquerque New Mexico commercial office building',
    startingPrice: 'Custom Pricing – Free On-Site Estimate',
    customBadge: true,
  },
];

export default function RGPSServices() {
  const router = useRouter();

  const handleBookNow = (service: Service) => {
    const params = new URLSearchParams({
      service: service.id,
      label: service.title,
      frequency: 'One-Time Service',
      price: service.customBadge ? 'Custom Quote' : service.startingPrice,
      includes: service.title,
      trashBin: service.isTrashBin ? '1' : '0',
      driveway: service.isDriveway ? '1' : '0',
      window: service.isWindow ? '1' : '0',
      houseWash: service.isHouseWash ? '1' : '0',
    });
    router.push(`/request?${params.toString()}`);
  };

  return (
    <section id="services" className="bg-light-gray py-20 px-4 sm:px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-teal-light text-teal font-bold text-sm px-4 py-2 rounded-full mb-4">
            What We Do
          </div>
          <h2 className="text-dark-text font-bold mb-4" style={{ fontSize: 'clamp(1.75rem, 4vw, 2.75rem)', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Our Professional Cleaning Services
          </h2>
          <p className="text-gray-text text-lg max-w-2xl mx-auto">
            Everything your property needs to stay clean, protected, and looking its best.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, i) => (
            <div
              key={service.id}
              className="group bg-white rounded-2xl overflow-hidden shadow-card transition-all duration-300 flex flex-col hover:shadow-card-lg hover:-translate-y-1"
              style={{ animationDelay: `${i * 0.05}s` }}
            >
              {/* Image */}
              <div className="relative h-44 overflow-hidden">
                <img
                  src={service.image}
                  alt={service.alt}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                <div className="absolute top-3 left-3 text-2xl bg-white/90 backdrop-blur-sm w-10 h-10 rounded-xl flex items-center justify-center shadow-sm">
                  {service.icon}
                </div>
              </div>

              {/* Content */}
              <div className="p-5 flex flex-col flex-1">
                <h3 className="font-bold text-dark-text text-base mb-2 leading-tight">
                  {service.title}
                </h3>
                <p className="text-gray-text text-sm leading-relaxed flex-1 mb-4">
                  {service.desc}
                </p>

                {/* Book Now Button */}
                <button
                  onClick={() => handleBookNow(service)}
                  className="w-full bg-teal hover:bg-teal-dark text-white font-bold text-sm py-2.5 rounded-xl transition-all hover:scale-[1.02]"
                >
                  Book Now
                </button>

                {/* Pricing */}
                <div className="mt-3 text-center">
                  {service.customBadge ? (
                    <span className="inline-block bg-teal-light text-teal text-xs font-bold px-3 py-1 rounded-full">
                      Custom Quote
                    </span>
                  ) : (
                    <p className="text-xs text-gray-500 leading-snug">
                      Starting at{' '}
                      <span className="font-bold text-teal">{service.startingPrice}</span>
                    </p>
                  )}

                  {service.addonBadge && (
                    <span className="inline-block mt-1.5 bg-teal-light text-teal text-xs font-semibold px-3 py-1 rounded-full">
                      {service.addonBadge}
                    </span>
                  )}

                  {!service.customBadge && (
                    <p className="text-xs text-gray-400 mt-1 italic leading-snug">
                      {service.priceNote
                        ? service.priceNote
                        : 'Final price depends on property size and condition.'}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Disclaimer */}
        <div className="mt-12 text-center max-w-3xl mx-auto">
          <p className="text-xs text-gray-400 leading-relaxed">
            All prices shown are starting prices. Final pricing may vary depending on the size, condition, accessibility, and specific needs of your property.{' '}
            <span className="text-gray-500 font-medium">No payment required to request service.</span>
          </p>
        </div>
      </div>
    </section>
  );
}
